import { Box, Button, Divider, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import React, { useContext, useState } from 'react'
import { collectionPageConfig as config } from './collection-page-config.ts'
import { useBehaviorObservable } from '@/common/react/observable-hooks.ts'
import { useDiRef } from '@/common/react/di-hooks.ts'
import { useOptionsPopover } from '@/core/popover/popover-hooks.tsx'
import { DefaultPopover } from '@/core/popover/DefaultPopover.tsx'
import { RenamableNodeName } from '@/objects/collections-tree/HierarchyNodeName.tsx'
import { ModalContext } from '@/core/modal/ModalContext.tsx'
import { DeleteTabConfirmModal } from './DeleteTabConfirmModal.tsx'
import {
  TypeOptionsPopoverItems,
  type TypeOptionPopoverItem,
} from '@/object-types/TypeOptionsPopoverItems.tsx'
import type { CollectionViewsService } from '@/objects/services/CollectionViewsService.ts'
import type { CollectionTabModel } from '@/core/gql/gql-models-override.ts'
import { useCollectionViewContext } from './top-bar/CollectionViewPageContext'
import { useTutorialTarget } from '@/tutorial/TutorialContext'

export function CollectionTabs() {
  const { collectionId } = useCollectionViewContext()
  const collectionViewsService = useDiRef<CollectionViewsService>('CollectionViewsService')
  const { openModal } = useContext(ModalContext)!

  const tabs = useBehaviorObservable(collectionViewsService.getTabs$(collectionId))
  const activeTabId = useBehaviorObservable(collectionViewsService.getActiveTabId$(collectionId))
  const activeTabIndex = tabs?.findIndex((x) => x.id === activeTabId) ?? 0

  const optionsPopover = useOptionsPopover<TypeOptionPopoverItem>()
  const tutorialTargetRef = useTutorialTarget('collection-tabs')
  const [renamingTabId, setRenamingTabId] = useState<number | null>(null)

  function onAddTabClicked() {
    void collectionViewsService.createTab(collectionId, 'New tab')
  }

  function onRenameSubmit(tabId: number, name: string) {
    const trimmed = name.trim()
    if (trimmed) {
      collectionViewsService.updateTab(collectionId, tabId, { name: trimmed })
    }
  }

  function onDeleteRequested(tab: CollectionTabModel) {
    void openModal<boolean>((modalProps) => (
      <DeleteTabConfirmModal {...modalProps} tabName={tab.name} />
    )).then((confirmed) => {
      if (confirmed) {
        void collectionViewsService.deleteTab(collectionId, tab.id)
      }
    })
  }

  function openTabPopover(e: React.MouseEvent<HTMLElement>, tab: CollectionTabModel) {
    e.preventDefault()
    e.stopPropagation()
    optionsPopover.openPopover(e.currentTarget, (option) => {
      if (option === 'Rename') {
        setRenamingTabId(tab.id)
      } else if (option === 'Delete') {
        onDeleteRequested(tab)
      }
    })
  }

  if (tabs === undefined) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, px: 1 }}>
        {Array.from({ length: config.skeletonTabCount }, (_, i) => (
          <Skeleton key={i} variant="rounded" width={72} height={28} />
        ))}
      </Box>
    )
  }

  const tabHoverContainerSx = {
    px: 1.25,
    py: 0.25,
    borderRadius: 1,
    transition: 'background-color 0.15s',
    '&:hover': { bgcolor: 'action.hover' },
  }
  return (
    <Box ref={tutorialTargetRef} sx={{ display: 'flex', alignItems: 'center', flex: 1, minHeight: config.tabHeight }}>
      <Tabs
        value={tabs.length > 0 ? activeTabIndex : false}
        onChange={(_, tabIndex: number) =>
          collectionViewsService.setActiveTabId(collectionId, tabs[tabIndex].id)
        }
        sx={{ minHeight: config.tabHeight }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            disableRipple
            disableFocusRipple
            sx={{
              minHeight: config.tabHeight,
              py: 0,
              textTransform: 'none',
              '&:hover': { bgcolor: 'transparent' },
              '&:focus-within': { bgcolor: 'transparent' },
            }}
            onClick={(e) =>
              renamingTabId !== tab.id && tab.id === activeTabId && openTabPopover(e, tab)
            }
            onContextMenu={(e) => openTabPopover(e, tab)}
            label={
              <Box sx={tabHoverContainerSx}>
                <RenamableNodeName
                  initialName={tab.name}
                  isRenaming={renamingTabId === tab.id}
                  setIsRenaming={(v) => setRenamingTabId(v ? tab.id : null)}
                  onRenameSubmit={(name) => onRenameSubmit(tab.id, name)}
                  renameMaxWidth={config.tabRenameMaxWidth}
                />
              </Box>
            }
          />
        ))}
      </Tabs>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Button
        size="small"
        onClick={onAddTabClicked}
        sx={[
          tabHoverContainerSx,
          {
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minWidth: 0,
            border: 'none',
          },
        ]}
      >
        <Box component="span" sx={{ fontSize: '1.4em', lineHeight: 1, mr: 0.5 }}>
          +
        </Box>
        <Typography variant="body1">Tab</Typography>
      </Button>
      <DefaultPopover
        anchorEl={optionsPopover.anchorEl}
        close={optionsPopover.closePopover}
        slots={{ popover: { disableRestoreFocus: true } }}
      >
        <TypeOptionsPopoverItems onItemSelected={optionsPopover.setResult} />
      </DefaultPopover>
    </Box>
  )
}
